import {
	sortByHeader,
	toggleRowActionCell,
} from '../../components/UI/MDC/DataTable/_utilities';
import { deepCloneObject, updateObject } from '../utility';
import { ACTION_TYPES } from './_actionTypes';
import { updateHeaderActionCell } from './_utility';

// The initial state of Redux store
const initialState = {
	actions: [],
	headers: [],
	rows: [],
	headerFilterBy: null,
};

/**
 * Reducer.
 * Updates state with existing property values and addition of new properties.
 * @param {Object} state current local state.
 * @param {Object} action dispatched action of interest.
 * @returns {Object} updated local state.
 */
const init = (state, action) => {
	return updateObject(state, action.payload);
};

/**
 * Reducer.
 * Updates `rows` of @param state with @param action property `payload`.
 * @function
 * @param {Object} state current local state.
 * @param {Object} action dispatched action of interest.
 * @returns {Object} updated local state.
 */
const setRows = (state, action) => {
	// Add rows to state
	let newState = updateObject(state, {
		rows: action.payload.rows,
	});

	// Sort
	const header = newState.headers.find(header => header.props.sort != null);
	newState = sortRows(newState, {
		payload: {
			header: header.Value,
			sortOrder: header.props.sort,
		},
	});

	// Filter only if current filter does not hide all rows
	// This is a check to ensure that selected chip is for a user that actually exists
	// Otherwise a bug occurs forcing user to refresh
	const temp = filterByUser(newState, {
		payload: action.payload.filterValue,
	});
	if (hasVisibleRows(temp.rows)) {
		newState = temp;
	}

	// Search
	// Not concerned with search causing no rows to display as this can be resolved by the user by erasing their search
	newState = indexRows(newState, { payload: action.payload.searchValue });

	// Update actions if needed
	const newActions = updateHeaderActionCell(
		newState.actions,
		newState.rows,
		0
	);

	return updateObject(newState, {
		actions: newActions,
	});
};

/**
 * Helper function.
 * Checks if @param rows is has at least one visible row.
 * @param {Array} rows Array of rows
 * @returns True if @param rows has at least one visible row. Otherwise, false.
 */
const hasVisibleRows = rows =>
	rows.some(row => row.displaySearched && row.displayFiltered);

/**
 * Reducer.
 * Toggles a single `row` of @param state based on @action `payload' property.
 * @function
 * @param {Object} state current local state.
 * @param {*} action dispatched action of interest. Expecting properties `e`, `index`.
 * @return {Object} updated local state.
 */
const toggleRow = (state, action) => {
	let newActions = state.actions;
	let newRows = state.rows;

	const row = action.payload;
	const indexRow = state.rows.findIndex(
		rowExisting => rowExisting.Key === row.Key
	);

	if (indexRow > -1) {
		newRows = toggleRowActionCell(newRows, row, 0);
	}

	newActions = updateHeaderActionCell(newActions, newRows, 0);

	return updateObject(state, {
		actions: newActions,
		rows: newRows,
	});
};

/**
 * Reducer.
 * Toggles all `rows` of @param state based on @action `payload' property.
 * @function
 * @param {Object} state current local state.
 * @param {*} action dispatched action of interest. Expecting properties `e`, `index`.
 * @return {Object} updated local state.
 */
const toggleAll = (state, action) => {
	const event = action.payload.e;
	const index = action.payload.index;

	const newActions = deepCloneObject(state.actions);
	const newRows = deepCloneObject(state.rows);

	const toggle = event?.target?.checked;
	for (let i = 0; i < newRows.length; i++) {
		if (newRows[i].displaySearched && newRows[i].displayFiltered) {
			newRows[i].ActionCells[index].Checked = toggle;
		}
	}

	// Update actions
	newActions[index].Header.Checked = toggle;

	return updateObject(state, {
		actions: newActions,
		rows: newRows,
	});
};

/**
 * Reducer.
 * Updates `rows` of @param state with index search criteria of @param action property `payload`.
 * Conditionally updates `actions` of @param state if header action cell does not match state of `rows` action cells.
 * @param {Object} state current local state.
 * @param {Object} action dispatched action of interest.
 * @returns {Object} updated local state.
 */
const indexRows = (state, action) => {
	const index = action.payload.toLowerCase();
	const arrHeaders = state.headers.map(header => header.Value);

	let newActions = state.actions;
	const newRows = deepCloneObject(state.rows);

	// Only display rows which show up in search
	newRows.forEach(row => {
		row.displaySearched = Object.keys(row.Cells)
			.filter(key => arrHeaders.includes(key))
			.some(key => row.Cells[key].Value?.toLowerCase().includes(index));
		row.ActionCells[0].Checked =
			row.ActionCells[0].Checked && row.displaySearched;
	});

	// Update actions
	newActions = updateHeaderActionCell(newActions, newRows, 0);

	return updateObject(state, {
		actions: newActions,
		rows: newRows,
	});
};

/**
 * Reducer.
 * Sorts `rows` of @param state against @param action properties `payload.header`, `payload.sortOrder`.
 * Invalid row indices always sorted to top.
 * Performs updates on both `rows` and `headers` state.
 * @function
 * @param {Object} state current local state.
 * @param {Object} action dispatched action of interest.
 * @returns {Object} conditionally updated local state.
 */
const sortRows = (state, action) => {
	let newHeaders = state.headers;
	let newRows = state.rows;

	// Find header
	let headerIndex = newHeaders.findIndex(
		objHeader => objHeader.Value === action.payload.header
	);
	let header = headerIndex > -1 ? newHeaders[headerIndex] : null;

	if (header?.props) {
		header.props.sort = action.payload.sortOrder ?? 1;

		// Reset all other headers
		newHeaders = newHeaders.map((objHeader, index) => {
			if (index !== headerIndex && objHeader.props) {
				objHeader.props.sort = null;
			}

			return objHeader;
		});

		// Sort rows
		newRows = sortByHeader(
			deepCloneObject(newRows),
			header.Value,
			header.props.sort
		);
	}

	return updateObject(state, {
		headers: newHeaders,
		rows: newRows,
	});
};

/**
 * Reducer.
 * Filters rows by a specific column and applied filter.
 * @param {Object} state current local state.
 * @param {Object} action dispatched action of interest.
 * @returns {Object} updated local state.
 */
const filterByUser = (state, action) => {
	const filter = action.payload.map(item =>
		item.replace('data-table__tool-bar__filter-', '')
	);

	let newActions = state.actions;
	const newRows = deepCloneObject(state.rows);

	if (filter.length > 0) {
		// Filter out rows with out a selected user
		newRows.forEach(row => {
			row.displayFiltered = filter.includes(
				row.Cells[state.headerFilterBy].Value?.toLowerCase()
			);
			row.ActionCells[0].Checked =
				row.ActionCells[0].Checked && row.displayFiltered;
		});
	} else {
		// Make all rows visible
		newRows.forEach(row => (row.displayFiltered = true));
	}

	// Update actions
	newActions = updateHeaderActionCell(newActions, newRows, 0);

	return updateObject(state, {
		actions: newActions,
		rows: newRows,
	});
};

/**
 * Root Reducer.
 * Resolves dispatched actions
 * @param {Object} state current local state.
 * @param {Object} action dispatched action of interest. Properties `type`, `payload` required.
 * @returns {Object} updated local state.
 */
const reducer = (state = initialState, action) => {
	switch (action.type) {
		case ACTION_TYPES.PENDING_APPROVALS_INIT:
			return init(state, action);
		case ACTION_TYPES.PENDING_APPROVALS_SET_ROWS:
			return setRows(state, action);
		case ACTION_TYPES.PENDING_APPROVALS_TOGGLE_ROW:
			return toggleRow(state, action);
		case ACTION_TYPES.PENDING_APPROVALS_TOGGLE_ALL:
			return toggleAll(state, action);
		case ACTION_TYPES.PENDING_APPROVALS_SEARCH_INDEX:
			return indexRows(state, action);
		case ACTION_TYPES.PENDING_APPROVALS_SORT_BY_HEADER:
			return sortRows(state, action);
		case ACTION_TYPES.PENDING_APPROVALS_FILTER_BY_USER:
			return filterByUser(state, action);
		default:
			return state;
	}
};

export default reducer;
